import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { WarehouseLocalizationCollectionItem } from '../../models/warehouse-localization-collection-item';
import { Observable, of } from 'rxjs';
import { WarehouseLocalizationListItemAction } from '../../enums/warehouse-localization-list-item-action';
import { WarehouseLocalizationDataSourceService } from '../../services/warehouse-localization-data-source.service';
import { WarehouseLocalizationListAction } from '../../enums/warehouse-localization-list-action';
import { Route, RouteParam } from '@web/common';
import { once } from '@web/utils';
import { WarehouseLocalizationCollectionFilter } from '../../models/warehouse-localization-collection-filter';

@Component({
  selector: 'app-warehouse-localization-list',
  imports: [TableComponent],
  templateUrl: './warehouse-localization-list.component.html',
  styleUrl: './warehouse-localization-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseLocalizationListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseLocalizationCollectionItem, WarehouseLocalizationCollectionFilter, WarehouseLocalizationListItemAction, never, WarehouseLocalizationListAction>).create();
  /* prettier-ignore */
  private readonly warehouseLocalizationDataSource = inject(WarehouseLocalizationDataSourceService);
  private readonly table = inject(TableService);

  public readonly createLocalisationVisibility = input<boolean>(false);

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseLocalizationCollectionItem,
    WarehouseLocalizationCollectionFilter,
    WarehouseLocalizationListItemAction,
    never,
    WarehouseLocalizationListAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder //
          .initText('label')
          .filterKey('label')
          .sortKey('label')
          .filterable(true)
          .sortable(true)
          .label('Etykieta')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .filterable(true)
          .sortable(true)
          .label('Data utworzenia')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(WarehouseLocalizationListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.WarehouseLocalizationCreate),
          )
          .label('Dodaj lokalizację')
          .visibility(() => of(this.createLocalisationVisibility()))
          .build(),
      ])
      .filters((builder) => [
        builder.initDateRange('createdAt').build(),
        builder.initText('label').build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(WarehouseLocalizationListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseLocalizationEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Edytuj')
          .icon(Icon.Pencil)
          .build(),
        builder
          .initCommand(WarehouseLocalizationListItemAction.Remove)
          .visibility((item) => item.canBeRemoved)
          .command((item) => this.remove(item))
          .tooltip('Usuń')
          .icon(Icon.Trash)
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<TableDataPayload<WarehouseLocalizationCollectionItem>> =>
          this.warehouseLocalizationDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private refresh(): void {
    this.table.refresh();
  }

  private remove(item: WarehouseLocalizationCollectionItem): void {
    this.warehouseLocalizationDataSource
      .confirmAndRemove(item.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }
}
